// Choice events during expeditions (CHANGELOG 6.1). The pool, rules and report
// texts live in expedition_events_mvp.json; this module validates it and holds the
// pure rules: which event, which option offline, the success chance and what an
// option does. The server applies the result (loot rolls, fights) to the run.

import { z } from 'zod';
import { ZONES } from '../content/zones';
import { clamp, type Rng } from '../rng';
import { zBackground, zStance, zZone } from '../schemas';
import { STANCES, type Attr, type BackgroundId, type ResourceId, type StackKind, type StackMap, type Stance, type ZoneId } from '../types';
import { STACKS, VAULT_GOODS } from '../content/resources';
import { RELIC_RULES } from '../items/relics';
import { wearableTier } from '../items/upgrade';
import { GAME } from '../constants';
import raw from './expedition_events_mvp.json';
import rawP4 from './expedition_events_p4.json';

// ---- Schema -----------------------------------------------------------------------------

const zAmount = z.record(z.string(), z.number());

const zOnWin = z
  .object({
    xp: z.number().optional(),
    lootRolls: z.number().int().min(0).optional(),
    resources: zAmount.optional(),
    /** Package 4, part J: the chance (%) of one Relic Shard for the win, and Rare+ weights × (1 + %) for the win's rolls. */
    relicPct: z.number().min(0).max(100).optional(),
    rarityBoostPct: z.number().min(0).max(200).optional(),
  })
  .strict();

const zFight = z
  .object({
    enemy: z.enum(['infected', 'raider', 'mutant', 'drone']),
    tierOffset: z.number().int().min(0).max(3).default(0),
    onWin: zOnWin.optional(),
    firstRoundDamagePct: z.number().min(0).max(100).optional(),
    /** Package 4, part J: a group (2–4), an elite one (a boss's strength, no boss rules). */
    count: z.number().int().min(1).max(4).optional(),
    elite: z.boolean().optional(),
  })
  .strict();

const zRarity = z.enum(['common', 'uncommon', 'rare', 'epic', 'legendary']);

/**
 * Package 4, part J: what a branch leaves for later runs. `gift`: after `runs` runs a small find waits on the path;
 * `toll_free`: no Tollmen toll on Route 9 for `runs` runs or `days` days; `zone_bonus`: the next `runs` runs in this zone
 * find more; `zone_easy`: the next `runs` runs in this zone meet less; `drone_next`: WARDEN watches the next run (more
 * drones); `mara_away`: Doc Mara does not talk to you for `days` days (the Patient 07 story waits); `vault_closed`: no
 * Vault deliveries for `days` days.
 */
const zLater = z
  .object({
    kind: z.enum(['gift', 'toll_free', 'zone_bonus', 'zone_easy', 'drone_next', 'mara_away', 'vault_closed']),
    runs: z.number().int().min(1).max(10).optional(),
    days: z.number().min(0.5).max(14).optional(),
  })
  .strict();

/** Everything an option (or its success / fail branch) can do. */
export const zEventEffects = z
  .object({
    text: z.string().optional(),
    xp: z.number().optional(),
    hpPct: z.number().min(-100).max(0).optional(),
    healPct: z.number().min(0).max(100).optional(),
    durationMin: z.number().optional(),
    encounterChancePct: z.number().optional(),
    lootRolls: z.number().int().min(0).max(5).optional(),
    rarityBoostPct: z.number().min(0).max(200).optional(),
    resources: zAmount.optional(),
    carriedResourcesPct: z.number().min(-100).max(0).optional(),
    durabilityPct: z.object({ armor: z.number().optional(), weapon: z.number().optional() }).strict().optional(),
    fight: zFight.optional(),
    /** Package 4, part I: this branch does a story chain's step (a step's own event; a fight must be won). */
    story: z.boolean().optional(),
    /** Package 4, part J: an injury or a fever (an injury only when none is on). */
    condition: z.enum(['deep_cut', 'cracked_ribs', 'sprained_ankle', 'concussion', 'fever']).optional(),
    /** Package 4, part J: an item of a rarity between `min` and `max` (most often `min`), of a kind; `worn`: its durability %. */
    item: z.object({ min: zRarity, max: zRarity, kind: z.enum(['any', 'weapon', 'ranged', 'armor', 'boots']).default('any'), worn: z.number().min(1).max(100).optional() }).strict().optional(),
    /** Package 4, part J: a Blueprint of a rarity between `min` and `max`. */
    blueprint: z.object({ min: zRarity, max: zRarity }).strict().optional(),
    /** Package 4, part J: Relic Shards (a rare branch). */
    relicShards: z.number().int().min(1).max(3).optional(),
    /** Package 4, part J: a fine in Scrap, out of the pack (as much as there is). */
    fine: z.number().int().min(1).max(200).optional(),
    /** Package 4, part J: WARDEN for the rest of the run: no drones (a good rating), or a Scanner along (more drones). */
    run: z.enum(['no_drones', 'scanner']).optional(),
    /** Package 4, part J: an effect on later runs. */
    later: zLater.optional(),
    /** Package 4, part J: a fight with the zone's named enemy (its rules; beaten, its sure Rare). */
    named: z.boolean().optional(),
    /** Package 4, part J: cures a fever (or an injury, a Deep Cut too). */
    cure: z.enum(['fever', 'injury']).optional(),
  })
  .strict();

export const zEventCheck = z
  .object({
    stat: z.enum(['STR', 'AGI', 'INT', 'VIT', 'PER']),
    base: z.number(),
    bonusBackground: zBackground.optional(),
    bonusStance: zStance.optional(),
    bonusPct: z.number().optional(),
  })
  .strict();

export const zEventOption = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    safe: z.boolean().optional(),
    cost: zAmount.optional(),
    effects: zEventEffects.default({}),
    check: zEventCheck.optional(),
    chance: z.number().min(0).max(100).optional(),
    success: zEventEffects.optional(),
    fail: zEventEffects.optional(),
    /** Package 4, part J: paid out of Storage, not the pack (a delivery to the Vault); unpayable, the option is not taken. */
    storageCost: zAmount.optional(),
    /** Package 4, part J: with a fever or an injury this branch is all the option does (WARDEN's checkpoint scans you). */
    sick: zEventEffects.optional(),
  })
  .strict();

/** 2. 10. 2026 (package 1, 6.a): how often an event comes up; `once`: once per survivor, ever. */
export const EVENT_FREQUENCIES = ['common', 'uncommon', 'rare', 'once'] as const;
export type EventFrequency = (typeof EVENT_FREQUENCIES)[number];

export const zExpeditionEvent = z
  .object({
    id: z.string().min(1),
    zones: z.array(z.union([zZone, z.literal('any')])).min(1),
    title: z.string().min(1),
    text: z.string().min(1),
    options: z.array(zEventOption).min(1),
    stanceDefault: z.object({ peaceful: z.string(), cautious: z.string(), aggressive: z.string(), shadow: z.string() }).strict(),
    frequency: z.enum(EVENT_FREQUENCIES).optional(),
    /** Package 4, part B: a decision in the middle of a long run (it waits for a player who is away). */
    decision: z.boolean().optional(),
    /** Package 4, part K: a Tollmen toll: its Scrap (cost, fine, label) × the zone's toll now (`tolledEvent`). */
    toll: z.boolean().optional(),
    /** Package 4, part K: the event comes only when Storage holds this (`vault_good`: any of the Vault's goods). */
    needsStorage: zAmount.optional(),
    /** Package 4, part J: the event does not come while one of these later effects is on (a Tollmen toll, a Vault delivery, Doc Mara). */
    notWhile: z.array(z.enum(['toll_free', 'vault_closed', 'mara_away'])).optional(),
  })
  .strict();

export const zEventsFile = z.object({
  version: z.number(),
  rules: z.record(z.string(), z.union([z.string(), z.number()])),
  events: z.array(zExpeditionEvent),
  /** Package 4, part J: the old events these replace (left out of the pool while the new ones are on). */
  replaces: z.array(z.string()).optional(),
});

export type EventEffects = z.infer<typeof zEventEffects>;
export type EventCheck = z.infer<typeof zEventCheck>;
export type EventOption = z.infer<typeof zEventOption>;
export type ExpeditionEvent = z.infer<typeof zExpeditionEvent>;
export type EventFight = z.infer<typeof zFight>;

// ---- Loading and validation ----------------------------------------------------------

function resourceKey(k: string): StackKind | 'random' | 'vault_good' | null {
  if (k === 'random') return 'random';
  // Package 4, part K: any of the Vault's goods (a delivery out of Storage).
  if (k === 'vault_good') return 'vault_good';
  // Krug 5 (K14.3): no aliases any more (lab_generator's "components" is Electronics in the file).
  return k in STACKS ? (k as StackKind) : null;
}

/**
 * Parses and checks the event file: schema, 4 stance defaults that point at real
 * options, at least one safe option, and resources the game knows.
 */
export function validateEvents(data: unknown = raw): { events: ExpeditionEvent[]; errors: string[] } {
  const parsed = zEventsFile.safeParse(data);
  if (!parsed.success) return { events: [], errors: parsed.error.issues.map((i) => `events: ${i.path.join('.')}: ${i.message}`) };
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const ev of parsed.data.events) {
    if (ids.has(ev.id)) errors.push(`event ${ev.id}: duplicate id`);
    ids.add(ev.id);
    const optIds = new Set(ev.options.map((o) => o.id));
    if (optIds.size !== ev.options.length) errors.push(`event ${ev.id}: duplicate option id`);
    for (const st of STANCES) if (!optIds.has(ev.stanceDefault[st])) errors.push(`event ${ev.id}: stanceDefault.${st} "${ev.stanceDefault[st]}" is not an option`);
    if (!ev.options.some((o) => o.safe)) errors.push(`event ${ev.id}: needs at least one option with safe: true`);
    for (const o of ev.options) {
      if (o.check && o.chance != null) errors.push(`event ${ev.id}/${o.id}: both check and chance`);
      if ((o.check || o.chance != null) && !o.success && !o.fail) errors.push(`event ${ev.id}/${o.id}: a roll without success or fail`);
      const all = [o.cost, o.storageCost, o.effects.resources, o.success?.resources, o.fail?.resources, o.sick?.resources, o.effects.fight?.onWin?.resources, o.success?.fight?.onWin?.resources, o.fail?.fight?.onWin?.resources, o.sick?.fight?.onWin?.resources];
      for (const r of all) for (const k of Object.keys(r ?? {})) if (!resourceKey(k) || (k === 'vault_good' && r !== o.storageCost)) errors.push(`event ${ev.id}/${o.id}: unknown resource "${k}"`);
    }
  }
  return { events: parsed.data.events, errors };
}

const loaded = validateEvents();
const loadedP4 = validateEvents(rawP4);
/** Package 4, part J: the 66 new events (`expedition_events_p4.json`) and the old ones they replace. */
export const P4_EVENTS: ExpeditionEvent[] = loadedP4.events;
export const P4_REPLACES: readonly string[] = (rawP4 as { replaces?: string[] }).replaces ?? [];
/** Both files' errors (an id in both files, too); the server refuses to start with any. */
export const EVENT_FILE_ERRORS: string[] = [...loaded.errors, ...loadedP4.errors, ...loadedP4.events.filter((e) => loaded.events.some((o) => o.id === e.id)).map((e) => `event ${e.id}: in both files`)];
/** The pool: the old events (without the ones replaced) and the new ones; empty if a file is invalid. */
export const EXPEDITION_EVENTS: ExpeditionEvent[] = [];
export const EVENT_BY_ID: Record<string, ExpeditionEvent> = {};

/**
 * Package 4, part J: the new events on (the default) or off (sims: the pool before part J). The pool and the lookup are
 * filled in place; every event of both files stays in the lookup (a run that met one still finds it).
 */
export function useP4Events(on: boolean) {
  const bad = loaded.errors.length > 0 || loadedP4.errors.length > 0;
  EXPEDITION_EVENTS.length = 0;
  if (!bad) EXPEDITION_EVENTS.push(...(on ? [...loaded.events.filter((e) => !P4_REPLACES.includes(e.id)), ...P4_EVENTS] : loaded.events));
  for (const e of [...loaded.events, ...P4_EVENTS]) if (!bad && !EVENT_BY_ID[e.id]) EVENT_BY_ID[e.id] = e;
}
useP4Events(true);

// ---- Rules ------------------------------------------------------------------------------

export const EVENT_RULES = {
  minutesPerEvent: 20,
  maxPerExpedition: 2,
  onlineChoiceSeconds: 60,
  lowHpPct: 0.3,
  chanceMin: 5,
  chanceMax: 95,
  /** Percent a check gains per point of its attribute above the base 15 (and loses below it). */
  checkPerPoint: 2,
  /** Per-tick encounter chance may not go past this with event modifiers. */
  encounterCap: 0.35,
  /** Enemy levels per tierOffset step. */
  levelsPerTier: 4,
} as const;

/**
 * 2. 10. 2026 (package 1, 6.a): which event comes up. The server keeps the player's last `memory` events
 * (`survivors.recentEvents`); the last min(`excludeMax`, half the zone's pool) never come again right away,
 * the others weigh 1 / (1 + times seen in that memory), a zone's own events `zoneWeight` times a general
 * one, and an event's `frequency` scales it. `on: false` (sims, VARIANT d and older): the pick before
 * (any event but this run's and the last 3, all alike).
 */
export const EVENT_PICK = {
  on: true,
  memory: 40,
  excludeMax: 8,
  zoneWeight: 2.5,
  frequency: { common: 1, uncommon: 0.5, rare: 0.2, once: 1 } as Record<EventFrequency, number>,
  /** The memory before package 1. */
  oldMemory: 3,
};

/** How many of the player's last events the server keeps. */
export const eventMemory = () => (EVENT_PICK.on ? EVENT_PICK.memory : EVENT_PICK.oldMemory);

/**
 * 3. 10. 2026 (package 2, 5.a): the most events a run gets, by its base duration: up to 60 minutes 2, 120
 * minutes 3, 240 minutes 4, 480 minutes 5. `on: false`: package 1, at most `EVENT_RULES.maxPerExpedition` (2).
 */
export const EVENT_COUNT = {
  on: true,
  byDuration: [
    [60, 2],
    [120, 3],
    [240, 4],
    [480, 5],
  ] as [number, number][],
};

/** The most events a run of this base duration gets. */
export function eventCap(baseMinutes: number): number {
  if (!EVENT_COUNT.on) return EVENT_RULES.maxPerExpedition;
  const rows = EVENT_COUNT.byDuration;
  return (rows.find(([upTo]) => baseMinutes <= upTo) ?? rows[rows.length - 1])[1];
}

/**
 * How many events a run gets: 1 per 20 minutes of its real length, at most `eventCap` of its base duration
 * (`baseMinutes`, the button; without it the real length); the onboarding run 2 gets one.
 */
export function eventCount(minutes: number, onboardingRun: 1 | 2 | null, baseMinutes = minutes): number {
  if (onboardingRun === 2) return 1;
  if (onboardingRun === 1) return 0;
  return Math.min(eventCap(baseMinutes), Math.floor(minutes / EVENT_RULES.minutesPerEvent));
}

/** Ticks where the events happen, spread over the run (never the first or the last tick). */
export function eventTicks(count: number, ticks: number, onboardingRun: 1 | 2 | null): number[] {
  if (onboardingRun === 2) return [Math.min(2, ticks - 1)];
  const out: number[] = [];
  for (let i = 1; i <= count; i++) out.push(clamp(Math.round((ticks * i) / (count + 1)), 1, Math.max(1, ticks - 2)));
  return [...new Set(out)];
}

/** The zone's pool: its own events and the general ones ("any"). */
export function eventPool(zone: ZoneId, pool = EXPEDITION_EVENTS): ExpeditionEvent[] {
  return pool.filter((e) => e.zones.includes('any') || e.zones.includes(zone));
}

/** 6.a: how many of the player's last events are left out (Downtown 8, Mountains 6, a secret zone 6). */
export function eventExcludeCount(poolSize: number): number {
  return EVENT_PICK.on ? Math.min(EVENT_PICK.excludeMax, Math.floor(poolSize / 2)) : EVENT_PICK.oldMemory;
}

/** 6.a: an event's weight in the pick (see EVENT_PICK). */
export function eventWeight(e: ExpeditionEvent, zone: ZoneId, recent: readonly string[]): number {
  const own = !e.zones.includes('any') && e.zones.includes(zone) ? EVENT_PICK.zoneWeight : 1;
  const seen = recent.reduce((n, id) => n + (id === e.id ? 1 : 0), 0);
  return (own * EVENT_PICK.frequency[e.frequency ?? 'common']) / (1 + seen);
}

/**
 * The event for a tick: from the zone's pool, never one of `exclude` (this run's), never a `once` event
 * in `opts.once`; without `recent` (the player's last events, oldest first) any of them, all alike.
 * Package 1 (6.a): the last few of `recent` are left out while others are there, the rest are weighed
 * (`eventWeight`). With `EVENT_PICK.on` false: the pick before (the last 3 left out, all alike).
 */
export function pickEvent(zone: ZoneId, exclude: string[], rng: Rng, pool = EXPEDITION_EVENTS, opts: { recent?: readonly string[]; once?: readonly string[]; on?: readonly string[]; storage?: StackMap } = {}): ExpeditionEvent | null {
  const all = eventPool(zone, pool);
  // Package 4, part J: an event that waits while a later effect is on (`opts.on`: the survivor's ones on now); part K: one
  // that needs something in Storage (a delivery to the Vault).
  const fits = all.filter(
    (e) =>
      !exclude.includes(e.id) &&
      !(e.frequency === 'once' && opts.once?.includes(e.id)) &&
      !e.notWhile?.some((k) => opts.on?.includes(k)) &&
      Object.entries(e.needsStorage ?? {}).every(([k, v]) => storageHas(opts.storage, k) >= v),
  );
  if (!fits.length) return null;
  const recent = opts.recent ?? [];
  const block = new Set(recent.slice(-eventExcludeCount(all.length)));
  const fresh = fits.filter((e) => !block.has(e.id));
  const from = fresh.length ? fresh : fits;
  if (!EVENT_PICK.on) return rng.pick(from);
  return rng.weighted(from.map((e) => [e, eventWeight(e, zone, recent)] as [ExpeditionEvent, number]));
}

const STAT_KEY: Record<EventCheck['stat'], Attr> = { STR: 'str', AGI: 'agi', INT: 'int', VIT: 'vit', PER: 'per' };

export interface EventActor {
  stats: Record<Attr, number>;
  background: BackgroundId;
  stance: Stance;
  hp: number;
  hpMax: number;
  /** What the survivor carries (costs are paid from it). */
  pack: StackMap;
  /** Package 4, part J: what is in Storage at the Camp (a delivery's cost); without it such an option cannot be paid. */
  storage?: StackMap;
  /** Package 4, part J: a fever or an injury on now (WARDEN's checkpoint). */
  sick?: boolean;
  /** 28. 10. 2026 (fb-balance): the survivor's level (EVENT_REWARD_RULES: rewards by the tier it wears; none: T1). */
  level?: number;
}

/**
 * clamp(base + (stat − 15) × 2 + bonus, 5, 95), counted from the base attribute 15
 * (krug 3, L2 / O4); the bonus needs the named background or stance.
 */
export function checkChance(check: EventCheck, actor: Pick<EventActor, 'stats' | 'background' | 'stance'>): number {
  const stat = actor.stats[STAT_KEY[check.stat]] ?? GAME.BASE_ATTR;
  const bonus = (check.bonusBackground && check.bonusBackground === actor.background) || (check.bonusStance && check.bonusStance === actor.stance) ? (check.bonusPct ?? 0) : 0;
  return clamp(check.base + (stat - GAME.BASE_ATTR) * EVENT_RULES.checkPerPoint + bonus, EVENT_RULES.chanceMin, EVENT_RULES.chanceMax);
}

/** Success chance of an option in percent (100 when there is no roll). */
export function optionChance(o: EventOption, actor: Pick<EventActor, 'stats' | 'background' | 'stance'>): number {
  if (o.check) return checkChance(o.check, actor);
  if (o.chance != null) return clamp(o.chance, 0, 100);
  return 100;
}

export function costOf(o: EventOption): StackMap {
  const out: StackMap = {};
  for (const [k, v] of Object.entries(o.cost ?? {})) {
    const key = resourceKey(k);
    if (key && key !== 'random' && key !== 'vault_good') out[key] = (out[key] ?? 0) + v;
  }
  return out;
}

/** Package 4, part K: how many of an amount Storage holds (`vault_good`: all of the Vault's goods together). */
export function storageHas(storage: StackMap | undefined, k: string): number {
  if (k === 'vault_good') return VAULT_GOODS.reduce((n, g) => n + (storage?.[g] ?? 0), 0);
  return storage?.[k as StackKind] ?? 0;
}

/** Package 4, part K: a cost out of Storage as real stacks (`vault_good`: the Vault's good Storage holds most of first). */
export function storageCostFor(cost: Record<string, number>, storage: StackMap | undefined): StackMap {
  const out: StackMap = {};
  for (const [k, v] of Object.entries(cost)) {
    if (k !== 'vault_good') {
      out[k as StackKind] = (out[k as StackKind] ?? 0) + v;
      continue;
    }
    let left = v;
    for (const g of [...VAULT_GOODS].sort((a, b) => (storage?.[b] ?? 0) - (storage?.[a] ?? 0))) {
      const n = Math.min(left, (storage?.[g] ?? 0) - (out[g] ?? 0));
      if (n > 0) {
        out[g] = (out[g] ?? 0) + n;
        left -= n;
      }
    }
  }
  return out;
}

export function canPay(o: EventOption, pack: StackMap, storage?: StackMap): boolean {
  if (!(Object.entries(costOf(o)) as [StackKind, number][]).every(([k, v]) => (pack[k] ?? 0) >= v)) return false;
  // Package 4, part J: a delivery out of Storage.
  return Object.entries(o.storageCost ?? {}).every(([k, v]) => storageHas(storage, k) >= v);
}

/** Food packed for the trip (krug 3, L3): taken from Storage at departure, what the run needs, what events used of it. */
export interface TripFood {
  packed: number;
  need: number;
  spent: number;
}

/** What an event cost can be paid from: the pack plus the food packed for the trip. */
export function payableStock(pack: StackMap, trip: TripFood | null | undefined): StackMap {
  const spare = trip ? Math.max(0, trip.packed - trip.spent) : 0;
  return spare > 0 ? { ...pack, food: (pack.food ?? 0) + spare } : pack;
}

/**
 * How a cost is paid: food the survivor found and carries goes first, then food packed
 * for the trip. Once the trip food no longer covers the run, the survivor is hungry
 * (−15% attack) until they are back.
 */
export function payCost(cost: StackMap, pack: StackMap, trip: TripFood | null | undefined): { fromPack: StackMap; fromTrip: number; hungry: boolean } {
  const fromPack: StackMap = { ...cost };
  let fromTrip = 0;
  const food = cost.food ?? 0;
  if (food > 0) {
    const carried = Math.min(food, pack.food ?? 0);
    fromTrip = food - carried;
    if (carried > 0) fromPack.food = carried;
    else delete fromPack.food;
  }
  const hungry = !!trip && fromTrip > 0 && trip.packed - trip.spent - fromTrip < trip.need;
  return { fromPack, fromTrip, hungry };
}

/** Package 4, part K: the line a toll event gets while the Tollmen hold Route 9 (part F2: the horde won there). */
export const TOLL_HELD_TEXT = 'The Tollmen held Route 9. Today the road costs twice as much.';

/**
 * Package 4, part K: a Tollmen toll at `mult` times its Scrap (the Tollmen held Route 9: 2): the costs and fines in Scrap
 * and the numbers in the options' labels ("Pay 40 Scrap"), and a line on the event's text. Other events as they are.
 */
export function tolledEvent(ev: ExpeditionEvent, mult: number): ExpeditionEvent {
  if (!ev.toll || mult === 1) return ev;
  const fx = (e: EventEffects | undefined) => (e && e.fine ? { ...e, fine: Math.round(e.fine * mult) } : e);
  return {
    ...ev,
    text: `${ev.text} ${TOLL_HELD_TEXT}`,
    options: ev.options.map((o) => ({
      ...o,
      label: o.label.replace(/(\d+) Scrap/, (_, n: string) => `${Math.round(Number(n) * mult)} Scrap`),
      ...(o.cost?.scrap ? { cost: { ...o.cost, scrap: Math.round(o.cost.scrap * mult) } } : {}),
      effects: fx(o.effects)!,
      ...(o.success ? { success: fx(o.success) } : {}),
      ...(o.fail ? { fail: fx(o.fail) } : {}),
    })),
  };
}

/** Package 4, part J: how sure an option is to end in a fight: 2 always (its effects, or both branches), 1 maybe, 0 never. */
export function fightRisk(o: EventOption): number {
  const f = (e: EventEffects | undefined) => !!(e?.fight || e?.named);
  if (f(o.effects) || (f(o.success) && f(o.fail))) return 2;
  return f(o.success) || f(o.fail) || f(o.sick) ? 1 : 0;
}

/**
 * The option taken without the player: the stance default; with HP under 30% or a
 * cost that cannot be paid, the first safe option (that can be paid). Package 4, part J: with no safe option to pay
 * for, the one least sure to end in a fight (no toll paid: sneak around, not fight three Tollmen).
 */
export function offlineChoice(ev: ExpeditionEvent, actor: Pick<EventActor, 'stance' | 'hp' | 'hpMax' | 'pack' | 'storage'>): EventOption {
  const def = ev.options.find((o) => o.id === ev.stanceDefault[actor.stance]) ?? ev.options[0];
  const lowHp = actor.hp < actor.hpMax * EVENT_RULES.lowHpPct;
  if (!lowHp && canPay(def, actor.pack, actor.storage)) return def;
  const safe = ev.options.find((o) => o.safe && canPay(o, actor.pack, actor.storage));
  if (safe) return safe;
  const free = ev.options.filter((o) => !o.cost && !o.storageCost);
  if (!free.length) return ev.options[0];
  return free.reduce((best, o) => (fightRisk(o) < fightRisk(best) ? o : best), free[0]);
}

// ---- Resolution --------------------------------------------------------------------------

export interface ResolvedFight {
  enemy: EventFight['enemy'];
  tierOffset: number;
  firstRoundDamagePct: number;
  onWin: { xp: number; lootRolls: number; resources: StackMap; relicPct: number; rarityBoostPct: number };
  /** Package 4, part J. */
  count: number;
  elite: boolean;
}

/** Package 4, part J: an event's elite foe (Subject 0001): tougher than the zone's own, weaker than a named one. */
export const EVENT_ELITE = { hpMult: 2, attackMult: 1.1 } as const;

export type EventLater = z.infer<typeof zLater>;

/** Package 4, part J: a later effect kept on the survivor (`record.later`): from which zone and event, runs to go or an end. */
export interface LaterEntry {
  kind: EventLater['kind'];
  zone: ZoneId;
  from: string;
  at: number;
  runs?: number;
  until?: number;
}

/**
 * Package 4, part J: what the later effects do. WARDEN watching (a Scanner along, or the next run): drones ×2; a zone that
 * finds more: +25% loot; one that meets less: encounters ×0.7; a gift on the path: three resource finds of the zone.
 */
export const LATER_RULES = { droneMult: 2, zoneLoot: 0.25, zoneEncounter: 0.7, giftFinds: 3 } as const;

/** A later effect on now (`zone`: for the effects of one zone, that zone's). */
export function laterActive(list: readonly LaterEntry[] | undefined, kind: EventLater['kind'], zone: ZoneId | undefined, now: number): boolean {
  return (list ?? []).some((x) => x.kind === kind && (x.until == null || x.until > now) && (x.runs == null || x.runs > 0) && (zone == null || x.zone === zone));
}

/** The kinds of later effects on now (an event with `notWhile` waits while one is). */
export function laterKindsOn(list: readonly LaterEntry[] | undefined, now: number): EventLater['kind'][] {
  return [...new Set((list ?? []).filter((x) => (x.until == null || x.until > now) && (x.runs == null || x.runs > 0)).map((x) => x.kind))];
}

/** One option, fully resolved: every number already scaled for the zone and every random pick made. */
export interface EventOutcome {
  eventId: string;
  optionId: string;
  label: string;
  /** Option text, then the success or fail text. */
  texts: string[];
  roll: { chance: number; ok: boolean } | null;
  cost: StackMap;
  xp: number;
  hpPct: number;
  healPct: number;
  durationMin: number;
  encounterChancePct: number;
  lootRolls: number;
  rarityBoostPct: number;
  resources: StackMap;
  carriedResourcesPct: number;
  durability: { armor: number; weapon: number };
  fight: ResolvedFight | null;
  /** Relic Shards found (6.6): only a risky choice that succeeds can turn one up. */
  relicShards: number;
  /** Package 4, part I: the branch taken does a story chain's step (its fight won, when it has one). */
  story?: boolean;
  /** Package 4, part J (see `zEventEffects`). */
  condition: EventEffects['condition'] | null;
  item: EventEffects['item'] | null;
  blueprint: EventEffects['blueprint'] | null;
  fine: number;
  run: EventEffects['run'] | null;
  later: EventLater[];
  named: boolean;
  storageCost: StackMap;
  cure: EventEffects['cure'] | null;
}

/** Resources and XP grow with zone danger: × (1 + 0.5 × (danger − 1)). */
export function dangerScale(zone: ZoneId): number {
  return 1 + 0.5 * (ZONES[zone].danger - 1);
}

/**
 * 28. 10. 2026 (owner, fb-balance): what an event gives grows with the survivor's tier too (the tier it can wear by
 * level, T1–T4: `byTier`), on top of the zone's danger: its XP and its resources (Scrap too) of a won roll's branch
 * and the fight it leads to (owner, 28. 10. 2026: the rewards of a success). Never an option's own effects (safe options,
 * trades), a cost, a fine, a loss, nor what a failed roll still gives.
 * Off until a 60-day simulation (progress-sim EVENT_REWARDS=new switches it on).
 */
export const EVENT_REWARD_RULES = { on: false, byTier: [1, 1.5, 2, 2.5] } as { on: boolean; byTier: number[] };

/** The tier factor on an event's rewards at a level (1 with the rule off or no level). */
export function eventRewardMult(level: number | undefined): number {
  if (!EVENT_REWARD_RULES.on || level == null) return 1;
  return EVENT_REWARD_RULES.byTier[wearableTier(level) - 1] ?? 1;
}

function scaledResources(r: Record<string, number> | undefined, zone: ZoneId, rng: Rng, tier = 1): StackMap {
  const out: StackMap = {};
  const m = dangerScale(zone) * tier;
  for (const [k, v] of Object.entries(r ?? {})) {
    const key = resourceKey(k);
    if (!key || key === 'vault_good') continue;
    if (key === 'random') {
      // n random basic resources from the zone table, one unit each (then scaled).
      for (let i = 0; i < v; i++) {
        const pick = rng.weighted(ZONES[zone].resources) as ResourceId;
        out[pick] = (out[pick] ?? 0) + 1;
      }
    } else out[key] = (out[key] ?? 0) + v;
  }
  // Package 4, part J: a MEND Shot, a Ration or a coupon is one thing, in any zone (only resources grow with danger).
  for (const k of Object.keys(out) as StackKind[]) if (STACKS[k]?.category === 'resource') out[k] = Math.max(1, Math.round((out[k] ?? 0) * m));
  return out;
}

/** `tier`: EVENT_REWARD_RULES' factor on the branch's XP and resources (1 on a failed roll's branch). */
function add(o: EventOutcome, e: EventEffects | undefined, zone: ZoneId, rng: Rng, tier = 1) {
  if (!e) return;
  if (e.text) o.texts.push(e.text);
  o.xp += Math.round((e.xp ?? 0) * dangerScale(zone) * tier);
  o.hpPct += e.hpPct ?? 0;
  o.healPct += e.healPct ?? 0;
  o.durationMin += e.durationMin ?? 0;
  o.encounterChancePct += e.encounterChancePct ?? 0;
  o.lootRolls += e.lootRolls ?? 0;
  o.rarityBoostPct = Math.max(o.rarityBoostPct, e.rarityBoostPct ?? 0);
  for (const [k, v] of Object.entries(scaledResources(e.resources, zone, rng, tier)) as [StackKind, number][]) o.resources[k] = (o.resources[k] ?? 0) + v;
  o.carriedResourcesPct += e.carriedResourcesPct ?? 0;
  o.durability.armor += e.durabilityPct?.armor ?? 0;
  o.durability.weapon += e.durabilityPct?.weapon ?? 0;
  if (e.story) o.story = true;
  // Package 4, part J.
  if (e.condition && !o.condition) o.condition = e.condition;
  if (e.item && !o.item) o.item = e.item;
  if (e.blueprint && !o.blueprint) o.blueprint = e.blueprint;
  o.relicShards += e.relicShards ?? 0;
  o.fine += e.fine ?? 0;
  if (e.run) o.run = e.run;
  if (e.later) o.later.push(e.later);
  if (e.named) o.named = true;
  if (e.cure) o.cure = e.cure;
  if (e.fight && !o.fight) {
    o.fight = {
      enemy: e.fight.enemy,
      tierOffset: e.fight.tierOffset ?? 0,
      firstRoundDamagePct: e.fight.firstRoundDamagePct ?? 0,
      onWin: { xp: Math.round((e.fight.onWin?.xp ?? 0) * dangerScale(zone) * tier), lootRolls: e.fight.onWin?.lootRolls ?? 0, resources: scaledResources(e.fight.onWin?.resources, zone, rng, tier), relicPct: e.fight.onWin?.relicPct ?? 0, rarityBoostPct: e.fight.onWin?.rarityBoostPct ?? 0 },
      count: e.fight.count ?? 1,
      elite: !!e.fight.elite,
    };
  }
}

/** Pay the cost, apply `effects`, then roll `check` / `chance` and add `success` or `fail`. */
export function resolveEventOption(ev: ExpeditionEvent, optionId: string, actor: EventActor, zone: ZoneId, rng: Rng): EventOutcome {
  const o = ev.options.find((x) => x.id === optionId);
  if (!o) throw new Error(`Unknown option ${optionId} for event ${ev.id}`);
  const out: EventOutcome = {
    eventId: ev.id,
    optionId: o.id,
    label: o.label,
    texts: [],
    roll: null,
    cost: costOf(o),
    xp: 0,
    hpPct: 0,
    healPct: 0,
    durationMin: 0,
    encounterChancePct: 0,
    lootRolls: 0,
    rarityBoostPct: 0,
    resources: {},
    carriedResourcesPct: 0,
    durability: { armor: 0, weapon: 0 },
    fight: null,
    relicShards: 0,
    condition: null,
    item: null,
    blueprint: null,
    fine: 0,
    run: null,
    later: [],
    named: false,
    storageCost: storageCostFor(o.storageCost ?? {}, actor.storage),
    cure: null,
  };
  // Package 4, part J: WARDEN's checkpoint: with a fever or an injury the option does only its `sick` branch.
  if (o.sick && actor.sick) {
    add(out, o.sick, zone, rng);
    return out;
  }
  // 28. 10. 2026 (fb-balance; owner: the rewards of a success only): the tier factor on a won roll's branch (and the fight
  // it leads to); an option's own effects (safe options, trades, deliveries) and a failed roll stay as the zone gives.
  const tier = eventRewardMult(actor.level);
  add(out, o.effects, zone, rng);
  if (o.check || o.chance != null) {
    const chance = optionChance(o, actor);
    const ok = rng.chance(chance / 100, `event ${ev.id}/${o.id}`);
    out.roll = { chance, ok };
    add(out, ok ? o.success : o.fail, zone, rng, ok ? tier : 1);
    if (ok && !o.safe && rng.chance(RELIC_RULES.eventChance, `event ${ev.id} relic`)) out.relicShards = Math.max(out.relicShards, 1);
  }
  return out;
}

/** HP after an event: never below 1, never above max. */
export function eventHp(hp: number, hpMax: number, out: Pick<EventOutcome, 'hpPct' | 'healPct'>): number {
  const next = hp + Math.round((hpMax * out.hpPct) / 100) + Math.round((hpMax * out.healPct) / 100);
  return clamp(next, 1, hpMax);
}
